import styled, { css, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';

// Keyframe animations
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(128, 0, 32, 0.3); }
  50% { box-shadow: 0 0 20px rgba(128, 0, 32, 0.5); }
`;

// Base Widget Card
export const WidgetCard = styled(motion.div)`
  background: ${props => props.$glass
    ? 'rgba(255, 255, 255, 0.7)'
    : 'var(--color-background-default)'};
  border-radius: ${borderRadius.lg};
  box-shadow: var(--shadow-md);
  padding: ${spacing.lg};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  backdrop-filter: ${props => props.$glass ? 'blur(10px)' : 'none'};
  border: ${props => props.$glass ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};

  ${props => props.$gradient && css`
    background: linear-gradient(135deg,
      var(--color-primary-main) 0%,
      var(--color-primary-light) 100%
    );
    color: var(--color-text-white);
  `}

  ${props => props.$highlighted && css`
    border-left: 4px solid var(--color-primary-main);
  `}

  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-2px);
  }
`;

// Widget Header
export const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.md};
  gap: ${spacing.md};
`;

export const WidgetTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$light ? 'var(--color-text-white)' : 'var(--color-text-primary)'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  svg {
    color: var(--color-primary-main);
  }
`;

export const WidgetSubtitle = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.$light ? 'rgba(255,255,255,0.8)' : 'var(--color-text-secondary)'};
  margin: ${spacing.xs} 0 0 0;
`;

export const WidgetActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

// Action Button
export const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$variant === 'primary'
    ? 'var(--color-primary-main)'
    : props.$variant === 'outline'
      ? 'transparent'
      : 'var(--color-background-paper)'};
  color: ${props => props.$variant === 'primary'
    ? 'var(--color-text-white)'
    : props.$variant === 'outline'
      ? 'var(--color-primary-main)'
      : 'var(--color-text-primary)'};
  border: ${props => props.$variant === 'outline'
    ? `1px solid var(--color-primary-main)`
    : 'none'};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$variant === 'primary'
      ? 'var(--color-primary-dark)'
      : props.$variant === 'outline'
        ? 'color-mix(in srgb, var(--color-primary-main) 6%, transparent)'
        : 'var(--color-border-light)'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }
`;

// Icon Button
export const IconButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$size === 'sm' ? '32px' : props.$size === 'lg' ? '48px' : '40px'};
  height: ${props => props.$size === 'sm' ? '32px' : props.$size === 'lg' ? '48px' : '40px'};
  background: ${props => props.$active ? 'color-mix(in srgb, var(--color-primary-main) 8%, transparent)' : 'transparent'};
  color: ${props => props.$active ? 'var(--color-primary-main)' : 'var(--color-text-secondary)'};
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: var(--color-background-paper);
    color: var(--color-primary-main);
  }

  svg {
    font-size: ${props => props.$size === 'sm' ? '16px' : props.$size === 'lg' ? '24px' : '20px'};
  }
`;

// Badge
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 ${spacing.xs};
  background: ${props => {
    switch (props.$variant) {
      case 'success': return colors.success.main;
      case 'warning': return colors.warning.main;
      case 'error': return colors.error.main;
      case 'info': return colors.info.main;
      default: return colors.primary.main;
    }
  }};
  color: var(--color-text-white);
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  border-radius: ${borderRadius.round};
  position: ${props => props.$floating ? 'absolute' : 'static'};
  top: ${props => props.$floating ? '-4px' : 'auto'};
  right: ${props => props.$floating ? '-4px' : 'auto'};
`;

// Status Badge
export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${props => {
    switch (props.$status) {
      case 'success':
      case 'completed':
      case 'paid':
        return colors.success.main + '15';
      case 'warning':
      case 'pending':
      case 'in_progress':
        return colors.warning.main + '15';
      case 'error':
      case 'critical':
      case 'overdue':
        return colors.error.main + '15';
      default:
        return colors.info.main + '15';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'success':
      case 'completed':
      case 'paid':
        return colors.success.main;
      case 'warning':
      case 'pending':
      case 'in_progress':
        return colors.warning.main;
      case 'error':
      case 'critical':
      case 'overdue':
        return colors.error.main;
      default:
        return colors.info.main;
    }
  }};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  border-radius: ${borderRadius.sm};
  text-transform: capitalize;
`;

// Priority Badge
export const PriorityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: ${props => {
    switch (props.$priority) {
      case 'high':
      case 'critical':
        return colors.error.main;
      case 'medium':
        return colors.warning.main;
      case 'low':
        return colors.success.main;
      default:
        return colors.info.main;
    }
  }};
  color: var(--color-text-white);
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  border-radius: ${borderRadius.sm};
  text-transform: uppercase;
`;

// Metric Display
export const MetricValue = styled.div`
  font-size: ${props => props.$size === 'lg'
    ? typography.fontSize.h2
    : props.$size === 'sm'
      ? typography.fontSize.lg
      : typography.fontSize.h3};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => props.$light ? 'var(--color-text-white)' : 'var(--color-text-primary)'};
  line-height: 1.2;
`;

export const MetricLabel = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.$light ? 'rgba(255,255,255,0.8)' : 'var(--color-text-secondary)'};
  margin-bottom: ${spacing.xs};
`;

export const MetricChange = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.$positive ? 'var(--color-success-main)' : 'var(--color-error-main)'};
  font-weight: ${typography.fontWeight.medium};

  svg {
    font-size: 14px;
  }
`;

// Grid Layouts
export const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns || 2}, 1fr);
  gap: ${props => props.$gap || spacing.lg};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(${props => Math.min(props.$columns || 2, 2)}, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => props.$minWidth || '200px'}, 1fr));
  gap: ${spacing.md};
`;

// List Items
export const ListItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${props => props.$active ? 'color-mix(in srgb, var(--color-primary-main) 3%, transparent)' : 'transparent'};
  border-radius: ${borderRadius.md};
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background: var(--color-background-paper);
  }

  ${props => props.$borderLeft && css`
    border-left: 3px solid ${
      props.$borderLeft === 'success' ? 'var(--color-success-main)' :
      props.$borderLeft === 'warning' ? 'var(--color-warning-main)' :
      props.$borderLeft === 'error' ? 'var(--color-error-main)' :
      'var(--color-primary-main)'
    };
  `}
`;

export const ListItemIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color ? `${props.$color}15` : 'color-mix(in srgb, var(--color-primary-main) 8%, transparent)'};
  color: ${props => props.$color || 'var(--color-primary-main)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }
`;

export const ListItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ListItemTitle = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ListItemSubtitle = styled.div`
  font-size: ${typography.fontSize.xs};
  color: var(--color-text-secondary);
  margin-top: 2px;
`;

export const ListItemValue = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
  text-align: right;
`;

// Progress Bar
export const ProgressBarContainer = styled.div`
  width: 100%;
  height: ${props => props.$height || '8px'};
  background: var(--color-neutral-200);
  border-radius: ${borderRadius.round};
  overflow: hidden;
`;

export const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: ${props => props.$color
    ? props.$color
    : `linear-gradient(90deg, var(--color-primary-main), var(--color-primary-light))`};
  border-radius: ${borderRadius.round};
`;

// Table Components
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background: var(--color-background-paper);
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid var(--color-border-light);
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-background-paper);
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.td`
  padding: ${spacing.sm} ${spacing.md};
  font-size: ${typography.fontSize.sm};
  color: var(--color-text-primary);
  text-align: ${props => props.$align || 'left'};
  white-space: ${props => props.$nowrap ? 'nowrap' : 'normal'};

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

export const TableHeader = styled.th`
  padding: ${spacing.sm} ${spacing.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-secondary);
  text-align: ${props => props.$align || 'left'};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

// Loading Skeleton
export const Skeleton = styled.div`
  background: linear-gradient(
    90deg,
    var(--color-neutral-200) 25%,
    var(--color-neutral-100) 50%,
    var(--color-neutral-200) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: ${props => props.$rounded ? borderRadius.round : borderRadius.sm};
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '20px'};
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xl};
  text-align: center;
  color: var(--color-text-secondary);

  svg {
    font-size: 48px;
    margin-bottom: ${spacing.md};
    opacity: 0.5;
  }
`;

// Divider
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: var(--color-border-light);
  margin: ${props => props.$spacing || spacing.md} 0;
`;

// Tooltip Container
export const TooltipContainer = styled(motion.div)`
  position: absolute;
  background: var(--color-secondary-main);
  color: var(--color-text-white);
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  white-space: nowrap;
  z-index: 1000;
  box-shadow: var(--shadow-md);

  &::before {
    content: '';
    position: absolute;
    border: 6px solid transparent;
    ${props => {
      switch (props.$position) {
        case 'top':
          return `
            bottom: -12px;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: var(--color-secondary-main);
          `;
        case 'bottom':
          return `
            top: -12px;
            left: 50%;
            transform: translateX(-50%);
            border-bottom-color: var(--color-secondary-main);
          `;
        case 'left':
          return `
            right: -12px;
            top: 50%;
            transform: translateY(-50%);
            border-left-color: var(--color-secondary-main);
          `;
        default:
          return `
            left: -12px;
            top: 50%;
            transform: translateY(-50%);
            border-right-color: var(--color-secondary-main);
          `;
      }
    }}
  }
`;

// Alert/Notification styles
export const AlertBox = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${props => {
    switch (props.$type) {
      case 'success': return colors.success.main + '10';
      case 'warning': return colors.warning.main + '10';
      case 'error': return colors.error.main + '10';
      default: return colors.info.main + '10';
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.$type) {
      case 'success': return colors.success.main;
      case 'warning': return colors.warning.main;
      case 'error': return colors.error.main;
      default: return colors.info.main;
    }
  }};
  border-radius: ${borderRadius.md};

  ${props => props.$pulse && css`
    animation: ${pulse} 2s infinite;
  `}
`;

// Gauge/Meter styles
export const GaugeContainer = styled.div`
  position: relative;
  width: ${props => props.$size || '120px'};
  height: ${props => props.$size || '120px'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const GaugeValue = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// Search Input
export const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  background: var(--color-background-paper);
  border: 1px solid var(--color-border-light);
  border-radius: ${borderRadius.md};
  transition: all 0.2s ease;

  &:focus-within {
    border-color: var(--color-primary-main);
    box-shadow: 0 0 0 3px var(--color-primary-main)20;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: ${typography.fontSize.sm};
    color: var(--color-text-primary);
    outline: none;

    &::placeholder {
      color: var(--color-text-tertiary);
    }
  }

  svg {
    color: var(--color-text-secondary);
  }
`;

// Keyboard Shortcut Badge
export const ShortcutBadge = styled.kbd`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: var(--color-neutral-100);
  border: 1px solid var(--color-border-light);
  border-radius: ${borderRadius.sm};
  font-family: ${typography.fontFamily.mono};
  font-size: 10px;
  color: var(--color-text-secondary);
`;

// Sparkline Container
export const SparklineContainer = styled.div`
  width: ${props => props.$width || '80px'};
  height: ${props => props.$height || '30px'};
  display: flex;
  align-items: center;
`;

// Glow Effect for critical items
export const GlowEffect = styled.div`
  ${props => props.$active && css`
    animation: ${glow} 2s infinite;
  `}
`;

// Flex utilities
export const FlexRow = styled.div`
  display: flex;
  align-items: ${props => props.$align || 'center'};
  justify-content: ${props => props.$justify || 'flex-start'};
  gap: ${props => props.$gap || spacing.md};
  flex-wrap: ${props => props.$wrap ? 'wrap' : 'nowrap'};
`;

export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$align || 'stretch'};
  justify-content: ${props => props.$justify || 'flex-start'};
  gap: ${props => props.$gap || spacing.sm};
`;

// Scroll Container
export const ScrollContainer = styled.div`
  max-height: ${props => props.$maxHeight || '300px'};
  overflow-y: auto;
  padding-right: ${spacing.sm};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--color-neutral-100);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-neutral-400);
    border-radius: 3px;

    &:hover {
      background: var(--color-neutral-500);
    }
  }
`;

const DashboardWidgets = {
  WidgetCard,
  WidgetHeader,
  WidgetTitle,
  WidgetSubtitle,
  WidgetActions,
  ActionButton,
  IconButton,
  Badge,
  StatusBadge,
  PriorityBadge,
  MetricValue,
  MetricLabel,
  MetricChange,
  WidgetGrid,
  StatGrid,
  ListItem,
  ListItemIcon,
  ListItemContent,
  ListItemTitle,
  ListItemSubtitle,
  ListItemValue,
  ProgressBarContainer,
  ProgressBarFill,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader,
  Skeleton,
  EmptyState,
  Divider,
  TooltipContainer,
  AlertBox,
  GaugeContainer,
  GaugeValue,
  SearchInput,
  ShortcutBadge,
  SparklineContainer,
  GlowEffect,
  FlexRow,
  FlexColumn,
  ScrollContainer
};

export default DashboardWidgets;
